// Analytics service for comprehensive event tracking
// This service provides methods to track various user interactions,
// fanning each event out to both GA4 and Vercel Analytics.
import { track as vercelTrack } from '@vercel/analytics';

// Initialize gtag function
const gtag = window.gtag || function() {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(arguments);
};

// Vercel Analytics custom events require flat properties (string, number,
// or boolean) - drop anything else (nested objects, arrays, null/undefined)
// rather than letting the SDK silently reject the whole event.
function toVercelProperties(parameters) {
  const props = {};
  for (const [key, value] of Object.entries(parameters)) {
    if (['string', 'number', 'boolean'].includes(typeof value)) {
      props[key] = value;
    }
  }
  return props;
}

class AnalyticsService {
  constructor() {
    this.isEnabled = typeof window !== 'undefined' && window.gtag;
    this.measurementId = 'G-7YV9W323D3';
  }

  // Page View Tracking
  trackPageView(pagePath, pageTitle) {
    // Vercel Analytics tracks page views itself via the <Analytics/> component.
    if (!this.isEnabled) return;

    gtag('config', this.measurementId, {
      page_path: pagePath,
      page_title: pageTitle,
    });

    console.log(`GA: Page view tracked - ${pageTitle} (${pagePath})`);
  }

  // Custom Event Tracking
  trackEvent(eventName, parameters = {}) {
    vercelTrack(eventName, toVercelProperties(parameters));

    if (!this.isEnabled) return;

    gtag('event', eventName, {
      ...parameters,
      timestamp: new Date().toISOString(),
    });

    console.log(`GA: Event tracked - ${eventName}`, parameters);
  }

  // Button Click Tracking
  trackButtonClick(buttonText, buttonLocation, additionalData = {}) {
    this.trackEvent('button_click', {
      button_text: buttonText,
      button_location: buttonLocation,
      event_category: 'engagement',
      ...additionalData
    });
  }

  // Navigation Tracking
  trackNavigation(fromPage, toPage, navigationMethod = 'click') {
    this.trackEvent('navigation', {
      from_page: fromPage,
      to_page: toPage,
      navigation_method: navigationMethod,
      event_category: 'navigation'
    });
  }

  // Link Click Tracking
  trackLinkClick(linkText, linkUrl, linkType = 'internal') {
    this.trackEvent('link_click', {
      link_text: linkText,
      link_url: linkUrl,
      link_type: linkType,
      event_category: 'engagement'
    });
  }

  // Download Tracking
  trackDownload(fileName, fileType) {
    this.trackEvent('file_download', {
      file_name: fileName,
      file_type: fileType,
      event_category: 'downloads'
    });
  }

  // Form Interaction Tracking
  trackFormStart(formName, formLocation) {
    this.trackEvent('form_start', {
      form_name: formName,
      form_location: formLocation,
      event_category: 'forms'
    });
  }

  trackFormSubmit(formName, formLocation, success = true) {
    this.trackEvent('form_submit', {
      form_name: formName,
      form_location: formLocation,
      success: success,
      event_category: 'forms'
    });
  }

  trackFormFieldFocus(formName, fieldName) {
    this.trackEvent('form_field_focus', {
      form_name: formName,
      field_name: fieldName,
      event_category: 'forms'
    });
  }

  // Scroll Tracking
  trackScrollDepth(depth, pagePath) {
    this.trackEvent('scroll_depth', {
      scroll_depth: depth,
      page_path: pagePath,
      event_category: 'engagement'
    });
  }

  // Time on Page Tracking
  trackTimeOnPage(pagePath, timeSpent) {
    this.trackEvent('time_on_page', {
      page_path: pagePath,
      time_spent_seconds: timeSpent,
      event_category: 'engagement'
    });
  }

  // Error Tracking
  trackError(errorMessage, errorLocation, errorType = 'javascript') {
    this.trackEvent('error', {
      error_message: errorMessage,
      error_location: errorLocation,
      error_type: errorType,
      event_category: 'errors'
    });
  }

  // Trading Dashboard Specific Events
  trackDashboardInteraction(interactionType, componentName, additionalData = {}) {
    this.trackEvent('dashboard_interaction', {
      interaction_type: interactionType,
      component_name: componentName,
      event_category: 'dashboard',
      ...additionalData
    });
  }

  trackChartInteraction(chartType, action, timeframe = null) {
    this.trackEvent('chart_interaction', {
      chart_type: chartType,
      action: action,
      timeframe: timeframe,
      event_category: 'charts'
    });
  }

  trackMetricView(metricName, metricValue, segment = null) {
    this.trackEvent('metric_view', {
      metric_name: metricName,
      metric_value: metricValue,
      segment: segment,
      event_category: 'metrics'
    });
  }

  // Contact and Engagement
  trackContactMethod(method, location) {
    this.trackEvent('contact_attempt', {
      contact_method: method,
      contact_location: location,
      event_category: 'contact'
    });
  }

  trackSocialMediaClick(platform, linkLocation) {
    this.trackEvent('social_media_click', {
      platform: platform,
      link_location: linkLocation,
      event_category: 'social'
    });
  }

  // User Engagement Metrics
  trackEngagement(engagementType, value, context = {}) {
    this.trackEvent('user_engagement', {
      engagement_type: engagementType,
      engagement_value: value,
      event_category: 'engagement',
      ...context
    });
  }

  // Session Quality Indicators
  trackSessionQuality(qualityIndicator, value) {
    this.trackEvent('session_quality', {
      quality_indicator: qualityIndicator,
      quality_value: value,
      event_category: 'session'
    });
  }

  // Content Interaction
  trackContentInteraction(contentType, contentTitle, action) {
    this.trackEvent('content_interaction', {
      content_type: contentType,
      content_title: contentTitle,
      action: action,
      event_category: 'content'
    });
  }

  // Search and Filter Actions
  trackSearch(searchTerm, searchLocation, resultsCount = null) {
    this.trackEvent('search', {
      search_term: searchTerm,
      search_location: searchLocation,
      results_count: resultsCount,
      event_category: 'search'
    });
  }

  trackFilter(filterType, filterValue, filterLocation) {
    this.trackEvent('filter_applied', {
      filter_type: filterType,
      filter_value: filterValue,
      filter_location: filterLocation,
      event_category: 'filters'
    });
  }
}

// Create and export a singleton instance
const analyticsService = new AnalyticsService();

export default analyticsService;

// Export specific tracking functions for convenience
export const {
  trackPageView,
  trackEvent,
  trackButtonClick,
  trackNavigation,
  trackLinkClick,
  trackDownload,
  trackFormStart,
  trackFormSubmit,
  trackFormFieldFocus,
  trackScrollDepth,
  trackTimeOnPage,
  trackError,
  trackDashboardInteraction,
  trackChartInteraction,
  trackMetricView,
  trackContactMethod,
  trackSocialMediaClick,
  trackEngagement,
  trackSessionQuality,
  trackContentInteraction,
  trackSearch,
  trackFilter
} = analyticsService;